import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { BadgeCheck, ChevronDown, ChevronLeft, Copy, Gift, ImageDown, LoaderCircle, QrCode, Share2, Sparkles, Users, X } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { api } from './api'
import { useApp } from './store'
import { INVITE_SHARE_DESCRIPTION, invitationShareTitle, updateShareMetadata } from './share'
import { hasWechatShareIntent, isWechatBrowser, publicInvitationUrl, serializeWechatShareIntent, WECHAT_SHARE_INTENT_KEY } from './wechat-share'
import WechatShareGuide, { ManualInviteLink } from './WechatShareGuide'
import referralPosterTemplate from './assets/referral-poster-template.jpg'
import type { ReferralFriend, ReferralInvitePreview, ReferralOverview } from './types'

const STATUS_TEXT: Record<string, string> = { BOUND: '待完成', QUALIFIED: '待发放', REWARDED: '已完成' }
const BUSINESS_TEXT: Record<string, string> = { MEAL: '点餐', RECHARGE: '充值', MEMBER_CARD: '购卡', POINT_DEPOSIT: '添柴', POINT_WITHDRAW: '取分' }
const REFERRAL_TABS = [{ id: 'invite', label: '邀请好友' }, { id: 'records', label: '邀请记录' }] as const
type ReferralTab = typeof REFERRAL_TABS[number]['id']

function loadPosterImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    if (/^https?:\/\//i.test(src)) image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('海报图片加载失败'))
    image.src = src
  })
}

function drawCover(ctx: CanvasRenderingContext2D, image: HTMLImageElement, width: number, height: number) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight)
  const sourceWidth = width / scale
  const sourceHeight = height / scale
  ctx.drawImage(image, (image.naturalWidth - sourceWidth) / 2, (image.naturalHeight - sourceHeight) / 2, sourceWidth, sourceHeight, 0, 0, width, height)
}

function drawPosterText(ctx: CanvasRenderingContext2D, text: string, y: number, maxWidth: number, preferredSize: number, minSize: number, color: string, weight = 600, family = '"STKaiti","KaiTi","Songti SC",serif', x = 375) {
  const value = text.trim()
  if (!value) return
  let size = preferredSize
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = color
  while (size > minSize) {
    ctx.font = `${weight} ${size}px ${family}`
    if (ctx.measureText(value).width <= maxWidth) break
    size -= 2
  }
  ctx.fillText(value, x, y, maxWidth)
}

export function InviteLandingPage() {
  const { code = '' } = useParams()
  const { member } = useApp()
  const navigate = useNavigate()
  const publicShareUrl = publicInvitationUrl(`/invite/${encodeURIComponent(code)}`, window.location.origin)
  const [isSharePreparation] = useState(() => {
    try { return !!publicShareUrl && isWechatBrowser(navigator.userAgent) && hasWechatShareIntent(sessionStorage.getItem(WECHAT_SHARE_INTENT_KEY), code) }
    catch { return false }
  })
  const [shareGuideOpen, setShareGuideOpen] = useState(isSharePreparation)
  const shareButtonRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (isSharePreparation) {
      try { sessionStorage.removeItem(WECHAT_SHARE_INTENT_KEY) } catch { /* Sharing remains usable without storage after arrival. */ }
    }
  }, [isSharePreparation])
  const preview = useQuery({ queryKey: ['referral-preview', code], queryFn: () => api<ReferralInvitePreview>(`/api/h5/referrals/invite/${encodeURIComponent(code)}`), enabled: !!code, retry: false })
  useEffect(() => {
    if (preview.data?.inviterNickname) updateShareMetadata(window.location.pathname, preview.data.inviterNickname)
  }, [preview.data?.inviterNickname])
  const resumeStarted = useRef(false)
  const bind = useMutation({
    mutationFn: () => api<{ message: string }>('/api/h5/referrals/bind', { method: 'POST', body: JSON.stringify({ inviteCode: code }) }),
    onSettled: () => {
      if (localStorage.getItem('h5ReferralInviteCode') === code.toUpperCase()) localStorage.removeItem('h5ReferralInviteCode')
    }
  })
  useEffect(() => {
    if (isSharePreparation) return
    const accepted = localStorage.getItem('h5ReferralInviteCode') === code.toUpperCase()
    if (!accepted || resumeStarted.current) return
    if (preview.isError || (member && !member.referralVisible)) {
      localStorage.removeItem('h5ReferralInviteCode')
      return
    }
    if (member?.referralVisible && preview.data && !bind.isPending && !bind.isSuccess && !bind.isError) {
      resumeStarted.current = true
      bind.mutate()
    }
  // Only resume an invitation the visitor explicitly accepted before login.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id, member?.referralVisible, preview.data?.campaignId, preview.isError, code, isSharePreparation])
  const acceptInvite = () => {
    if (isSharePreparation) return
    if (member) { bind.mutate(); return }
    localStorage.setItem('h5ReferralInviteCode', code.toUpperCase())
    navigate('/login')
  }
  return <main className="invite-landing">
    <div className="invite-glow" />
    <header><div className="brand-mark">86</div><span>好友邀请</span></header>
    {preview.isLoading && <section className="invite-state"><LoaderCircle className="spin" /><p>正在读取邀请</p></section>}
    {preview.isError && <section className="invite-state"><h1>邀请暂不可用</h1><p>{preview.error instanceof Error ? preview.error.message : '活动已结束或链接无效'}</p><button onClick={() => navigate(isSharePreparation ? '/referral' : '/')}>{isSharePreparation ? '返回邀请中心' : '返回首页'}</button></section>}
    {preview.data && <section className="invite-landing-card">
      <small>{preview.data.campaignName}</small>
      <h1>{preview.data.posterTitle || '好友邀你上山'}</h1>
      <p><strong>{preview.data.inviterNickname}</strong> 邀请你成为山友</p>
      <div className="invite-reward-sections">
        <section className="invite-reward-section" aria-labelledby="invite-base-title">
          <h2 id="invite-base-title">首次进店基础奖励</h2>
          <p>首次进店消费可得，次日早晨 6:00（北京时间）到账</p>
          <InviteRewardValues points={preview.data.baseNewPoints} wine={preview.data.baseNewWineCoupon} giftBalance={preview.data.baseNewGiftBalance} />
        </section>
        <section className="invite-reward-section invite-reward-extra" aria-labelledby="invite-extra-title">
          <h2 id="invite-extra-title">邀请额外奖励</h2>
          <p>接受邀请并首次进店消费，额外加赠即时到账</p>
          <InviteRewardValues points={preview.data.inviteePoints} wine={preview.data.inviteeWineCoupon} giftBalance={preview.data.inviteeGiftBalance} />
        </section>
      </div>
      <p className="invite-reward-note">奖励满足条件后自动到账，券核销不计入新客。</p>
      {isSharePreparation ? <div className="invite-share-actions">
        <p>这是你的邀请预览，好友打开后可接受邀请</p>
        <button ref={shareButtonRef} className="invite-primary" onClick={() => setShareGuideOpen(true)}><Share2 />分享给好友</button>
        <button className="invite-share-back" onClick={() => navigate('/referral', { replace: true })}>返回邀请中心 · 海报 / 二维码</button>
      </div> : <>
      {!member && <button className="invite-primary" onClick={acceptInvite}>接受邀请，登录领取</button>}
      {member?.referralVisible && bind.isIdle && <button className="invite-primary" onClick={acceptInvite}>接受邀请</button>}
      {member && !member.referralVisible && <><p className="invite-bind-error" role="status">敬请期待</p><button className="invite-primary" onClick={() => navigate('/')}>返回首页</button></>}
      {member && bind.isPending && <button className="invite-primary" disabled><LoaderCircle className="spin" />正在绑定邀请关系</button>}
      {member && bind.isSuccess && <><div className="invite-success" role="status"><BadgeCheck />邀请关系已绑定</div><button className="invite-primary" onClick={() => navigate('/referral', { replace: true })}>查看邀请活动</button></>}
      {member && bind.isError && <><p className="invite-bind-error" role="alert">{bind.error instanceof Error ? bind.error.message : '绑定失败'}</p><button className="invite-primary" onClick={() => navigate('/referral', { replace: true })}>进入邀请中心</button></>}
      </>}
    </section>}
    {isSharePreparation && shareGuideOpen && preview.data && !preview.isError && publicShareUrl && <WechatShareGuide
      inviteUrl={publicShareUrl}
      inviterNickname={preview.data.inviterNickname}
      onClose={() => { setShareGuideOpen(false); window.requestAnimationFrame(() => shareButtonRef.current?.focus()) }}
      onReturn={() => navigate('/referral', { replace: true })}
    />}
  </main>
}

function InviteRewardValues({ points = 0, wine = 0, giftBalance = 0 }: { points?: number; wine?: number; giftBalance?: number }) {
  if (!(points > 0 || wine > 0 || giftBalance > 0)) return <p className="invite-reward-none">暂无配置奖励</p>
  return <ul className="invite-reward-values" role="list">
    {points > 0 && <li><strong>{points}</strong> 柴火</li>}
    {wine > 0 && <li><strong>{wine}</strong> 张酒水券</li>}
    {giftBalance > 0 && <li><strong>¥{Number(Number(giftBalance).toFixed(2))}</strong> 赠送余额</li>}
  </ul>
}

export function ReferralCenterPage() {
  const navigate = useNavigate()
  const { member } = useApp()
  const overview = useQuery({ queryKey: ['referral-overview', member?.id], queryFn: () => api<ReferralOverview>('/api/h5/referrals/overview'), retry: false })
  const [activeTab, setActiveTab] = useState<ReferralTab>('invite')
  const [mode, setMode] = useState<'qr' | 'poster' | null>('poster')
  const [qrData, setQrData] = useState('')
  const [posterData, setPosterData] = useState('')
  const [notice, setNotice] = useState('')
  const [imageError, setImageError] = useState('')
  const [manualCopy, setManualCopy] = useState(false)
  const noticeTimer = useRef<number | undefined>(undefined)
  const inviteUrl = useMemo(() => overview.data?.invitePath ? `${window.location.origin}${overview.data.invitePath}` : '', [overview.data?.invitePath])
  const needsQr = activeTab === 'invite' && mode !== null
  useEffect(() => {
    if (!inviteUrl || !needsQr) return
    let cancelled = false
    setImageError('')
    QRCode.toDataURL(inviteUrl, { width: 720, margin: 2, color: { dark: '#17130c', light: '#fffaf0' } })
      .then(value => { if (!cancelled) setQrData(value) })
      .catch(() => { if (!cancelled) setImageError('二维码生成失败，请收起后重试') })
    return () => { cancelled = true }
  }, [inviteUrl, needsQr])
  useEffect(() => {
    const campaign = overview.data?.campaign
    if (activeTab !== 'invite' || mode !== 'poster' || !campaign || !qrData) return
    let cancelled = false
    setImageError('')
    const renderPoster = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 750; canvas.height = 1000
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('无法生成海报')
      let background: HTMLImageElement
      try {
        background = await loadPosterImage(campaign.posterBackgroundUrl || referralPosterTemplate)
      } catch {
        background = await loadPosterImage(referralPosterTemplate)
      }
      const qr = await loadPosterImage(qrData)
      if (cancelled) return
      drawCover(ctx, background, 750, 1000)
      drawPosterText(ctx, campaign.posterBrandText || '86 上山', 77, 270, 40, 28, '#efc789', 500)
      drawPosterText(ctx, campaign.posterTitle || '好友邀你上山', 215, 680, 88, 48, '#f0c98b', 700)
      drawPosterText(ctx, campaign.posterSubtitle || '好友同行 · 双方有礼', 330, 540, 28, 18, '#e4c997', 600)
      drawPosterText(ctx, campaign.posterRewardTitle || '新客基础酒券', 505, 390, 43, 30, '#f5e5c5', 600, undefined, 285)
      drawPosterText(ctx, '+', 566, 80, 40, 34, '#e3bd75', 600, 'serif', 285)
      drawPosterText(ctx, campaign.posterRewardSubtitle || '邀请加码礼', 620, 390, 43, 30, '#f5e5c5', 600, undefined, 285)
      drawPosterText(ctx, campaign.posterClaimText || '完成首次有效业务即可领取', 716, 340, 22, 15, '#342719', 600, undefined, 285)
      drawPosterText(ctx, `${member?.nickname || '好友'}  ${campaign.posterInviteText || '邀请你加入'}`, 825, 390, 30, 20, '#f1d29a', 600, undefined, 270)
      drawPosterText(ctx, campaign.posterQrHint || '长按识别二维码', 888, 330, 23, 16, '#e5c58c', 500, undefined, 260)
      ctx.drawImage(qr, 510, 786, 128, 128)
      drawPosterText(ctx, campaign.posterFooterText || '具体规则以活动页面为准', 963, 420, 17, 13, '#8d7960', 400)
      setPosterData(canvas.toDataURL('image/jpeg', 0.94))
    }
    void renderPoster().catch(() => { if (!cancelled) setImageError('海报生成失败，请收起后重试') })
    return () => { cancelled = true }
  }, [activeTab, mode, member?.nickname, overview.data?.campaign, qrData])
  useEffect(() => () => window.clearTimeout(noticeTimer.current), [])
  const flash = (text: string) => {
    window.clearTimeout(noticeTimer.current)
    setNotice(text)
    noticeTimer.current = window.setTimeout(() => setNotice(''), 2400)
  }
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(inviteUrl); setManualCopy(false); flash('邀请链接已复制') }
    catch { setManualCopy(true); flash('复制失败，请长按下方链接，或使用二维码或邀请海报分享') }
  }
  const shareLink = async () => {
    if (!inviteUrl) return
    if (isWechatBrowser(navigator.userAgent)) {
      const publicUrl = publicInvitationUrl(inviteUrl, window.location.origin)
      if (!publicUrl) { flash('邀请链接暂不可用，请使用二维码或邀请海报分享'); return }
      try {
        const code = publicUrl.slice(publicUrl.lastIndexOf('/') + 1)
        sessionStorage.setItem(WECHAT_SHARE_INTENT_KEY, serializeWechatShareIntent(code))
        // A fresh document gives WeChat the public URL, never the protected center's entry URL.
        window.location.assign(publicUrl)
      } catch {
        setManualCopy(true)
        flash('暂时无法打开分享引导，请复制下方邀请链接或使用邀请海报')
      }
      return
    }
    try {
      if (navigator.share) await navigator.share({ title: invitationShareTitle(member?.nickname), text: INVITE_SHARE_DESCRIPTION, url: inviteUrl })
      else await copyLink()
    } catch (error) {
      if (!(error instanceof Error && error.name === 'AbortError')) flash('暂时无法分享，请尝试复制链接或二维码')
    }
  }
  const selectTab = (tab: ReferralTab) => {
    setActiveTab(tab)
    setNotice('')
  }
  if (overview.isLoading) return <main className="referral-page"><div className="referral-loading"><LoaderCircle className="spin" />加载邀请活动</div></main>
  if (overview.isError) return <main className="referral-page"><ReferralHeader onBack={() => navigate(-1)} /><section className="referral-empty"><h2>邀请活动暂不可用</h2><p>{overview.error instanceof Error ? overview.error.message : '请稍后再试'}</p></section></main>
  if (!overview.data?.available || !overview.data.campaign) return <main className="referral-page"><ReferralHeader onBack={() => navigate(-1)} /><section className="referral-empty"><Sparkles /><h2>活动暂未开启</h2><p>活动开启后可在这里生成邀请链接、二维码和海报。</p></section></main>
  const data = overview.data
  const campaign = data.campaign!
  return <main className="referral-page">
    <ReferralHeader onBack={() => navigate(-1)} />
    <div className="referral-tabs" role="tablist" aria-label="邀请中心" onKeyDown={event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      event.preventDefault()
      const nextTab = event.key === 'Home' ? 'invite' : event.key === 'End' ? 'records' : activeTab === 'invite' ? 'records' : 'invite'
      selectTab(nextTab)
      event.currentTarget.querySelector<HTMLButtonElement>(`#referral-tab-${nextTab}`)?.focus()
    }}>
      {REFERRAL_TABS.map(tab => <button key={tab.id} id={`referral-tab-${tab.id}`} type="button" role="tab" aria-selected={activeTab === tab.id} aria-controls={`referral-panel-${tab.id}`} tabIndex={activeTab === tab.id ? 0 : -1} onClick={() => selectTab(tab.id)}>{tab.label}</button>)}
    </div>
    <div id="referral-panel-invite" className="referral-tab-panel" role="tabpanel" aria-labelledby="referral-tab-invite" hidden={activeTab !== 'invite'}>
      <section className="referral-rewards" aria-labelledby="referral-rewards-title">
        <h2 id="referral-rewards-title"><Gift />双方预计奖励</h2>
        <dl>
          <RewardRow label="你" points={campaign.inviterPoints} wine={campaign.inviterWineCoupon} giftBalance={campaign.inviterGiftBalance} />
          <RewardRow label="好友" points={Number(campaign.baseNewPoints) + Number(campaign.inviteePoints)} wine={Number(campaign.baseNewWineCoupon) + Number(campaign.inviteeWineCoupon)} giftBalance={Number(campaign.baseNewGiftBalance) + Number(campaign.inviteeGiftBalance)} />
        </dl>
        <p className="referral-reward-condition"><BadgeCheck />好友完成首次有效业务，双方奖励立即到账</p>
      </section>
      <section className="referral-sharing" aria-label="邀请分享">
        <button className="referral-main-action" disabled={!inviteUrl} onClick={shareLink}><Share2 />分享给好友</button>
        <div className="referral-share-options">
          <button disabled={!inviteUrl} aria-expanded={mode === 'poster'} aria-controls="referral-share-preview" onClick={() => setMode(mode === 'poster' ? null : 'poster')}><ImageDown />邀请海报</button>
          <button disabled={!inviteUrl} aria-expanded={mode === 'qr'} aria-controls="referral-share-preview" onClick={() => setMode(mode === 'qr' ? null : 'qr')}><QrCode />二维码</button>
          <button disabled={!inviteUrl} onClick={copyLink}><Copy />复制链接</button>
        </div>
        {manualCopy && <ManualInviteLink url={inviteUrl} />}
        {mode && <div id="referral-share-preview" className="referral-share-preview">
          <header><h2>{mode === 'qr' ? '专属邀请二维码' : '邀请海报'}</h2><button aria-label="收起分享预览" onClick={() => setMode(null)}><X /></button></header>
          {!inviteUrl ? <p className="referral-image-error">邀请链接暂不可用，请稍后重试</p> : imageError ? <p role="alert" className="referral-image-error">{imageError}</p> : mode === 'qr'
            ? <div className="referral-qr-panel">{qrData ? <img src={qrData} alt="专属邀请二维码" /> : <LoaderCircle aria-label="正在生成二维码" className="spin" />}<p>好友扫码查看奖励，接受邀请并登录后绑定</p></div>
            : <div className="referral-poster-panel">{posterData ? <><img src={posterData} alt="86上山邀请海报" /><p>长按海报保存，分享给好友或朋友圈</p></> : <LoaderCircle aria-label="正在生成海报" className="spin" />}</div>}
        </div>}
      </section>
      <details className="referral-rules" open>
        <summary>奖励说明<ChevronDown /></summary>
        <div>
          <p>当前活动：{campaign.name}。页面展示双方预计可获得的奖励，具体以实际到账为准。</p>
          <p>好友奖励由首次进店基础奖励与接受邀请加码奖励叠加，页面已合并展示。</p>
          <p>好友可以是已注册但从未完成核心业务的会员。首次完成点餐、充值、购卡、存分或取分后，系统立即向双方发放邀请奖励；首次进店基础奖励于次日早晨 6:00（北京时间）发放。</p>
          <p>券核销、注册登录、签到抽奖、娱乐预约以及未支付、已取消、已退款业务不计入新客完成。</p>
        </div>
      </details>
    </div>
    <div id="referral-panel-records" className="referral-tab-panel" role="tabpanel" aria-labelledby="referral-tab-records" hidden={activeTab !== 'records'} tabIndex={0}>
      <section className="referral-summary"><div><Users /><span>已邀请<b>{data.summary.invitedCount || 0}</b></span></div><div><Sparkles /><span>待完成<b>{data.summary.pendingCount || 0}</b></span></div><div><BadgeCheck /><span>已完成<b>{data.summary.rewardedCount || 0}</b></span></div></section>
      <section className="referral-friends"><header><h2>好友进度</h2></header>{data.friends.length ? data.friends.map(friend => <FriendRow key={friend.id} friend={friend} />) : <div className="referral-no-friend"><Users /><p>还没有邀请记录</p><span>邀请第一位好友，一起领取奖励</span><button onClick={() => {
        selectTab('invite')
        document.getElementById('referral-tab-invite')?.focus()
        window.scrollTo({ top: 0, behavior: 'auto' })
      }}>去邀请好友</button></div>}</section>
    </div>
    {notice && <div className="referral-toast" role="status">{notice}</div>}
  </main>
}

function RewardRow({ label, points, wine, giftBalance }: { label: string; points: number; wine: number; giftBalance: number }) {
  return <div className="referral-reward-row">
    <dt>{label}</dt>
    <dd>
      {points > 0 && <span><strong>{points}</strong> 柴火</span>}
      {wine > 0 && <span><strong>{wine}</strong> 张酒水券</span>}
      {giftBalance > 0 && <span><strong>¥{Number(Number(giftBalance).toFixed(2))}</strong> 赠送余额</span>}
      {!(points > 0 || wine > 0 || giftBalance > 0) && <span className="referral-reward-none">暂无配置奖励</span>}
    </dd>
  </div>
}

function FriendRow({ friend }: { friend: ReferralFriend }) {
  const business = friend.businessType ? BUSINESS_TEXT[friend.businessType] || friend.businessType : ''
  return <div className="referral-friend-row">
    <div className="referral-friend-heading">
      <b>{friend.inviteeNickname || '会员'}</b>
      <em className={`status-${friend.status.toLowerCase()}`}>{business ? `${business} · ` : ''}{STATUS_TEXT[friend.status] || friend.status}</em>
    </div>
    <div className="referral-friend-contact"><span>UID {friend.inviteeShortId || '—'}</span><span>{friend.inviteePhone || '—'}</span></div>
  </div>
}

function ReferralHeader({ onBack }: { onBack: () => void }) {
  return <header className="referral-header"><button aria-label="返回" onClick={onBack}><ChevronLeft /></button><div><h1>邀请有礼</h1><p>邀请好友 · 双方得礼</p></div><i /></header>
}
